/* eslint-disable react/button-has-type, jsx-a11y/label-has-associated-control */
/* eslint-disable no-nested-ternary, jsx-a11y/no-autofocus, react/no-unescaped-entities */
/* eslint-disable no-unused-vars, no-useless-return, curly, nonblock-statement-body-position */
/* eslint-disable no-confusing-arrow */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { mdiDownload, mdiEye } from '@mdi/js';
import Icon from '@mdi/react';
import { SUB_SCREENS, ATTACH_CATS } from '../../constants';
import { CloseIcon, DeleteIcon } from '../CommonUI';
import { apiCall, API_BASE_URL } from '../../utils/api';
import { apiClient } from '../../../../config/axios.config';
import { BASE_EMPLOYEE_URL } from '../../../../store/apiConfig';
import { FileUtil } from '../../../../utils';
import DisplayModal from '../../../../components/Common/DisplayModal/DisplayModal';
import EmployeeMasterDeleteConfirm, {
  emBasenameOnly,
} from './EmployeeMasterDeleteConfirm';

/** Must match BE `EMPLOYEE_MASTER_ATTACHMENT_TYPE` (multer upload folder). */
const EMPLOYEE_MASTER_DOWNLOAD_TYPE = 'Employee_Master';
const EMPLOYEE_ATTACHMENT_TYP = 'EMPDOC';
const clean = value => (typeof value === 'string' ? value.trim() : value);

const splitAttachmentRemarks = rawRemarks => {
  const value = String(rawRemarks || '').trim();
  if (!value) return { categoryCode: '', remarks: '' };
  const [first, ...rest] = value.split('|');
  if (first && /^JNDOC/i.test(first)) {
    return {
      categoryCode: first.trim(),
      remarks: rest.join('|').trim(),
    };
  }
  return { categoryCode: '', remarks: value };
};

/**
 * Attachments Sub-Screen
 * Upload multiple files with remarks for each attachment.
 * ┌──────────────────────────────────────────────────────────────┐
 * │  GET    /api/attachments/:empCode           → [{ id, ... }] │
 * │  POST   /api/attachments/:empCode           (multipart)     │
 * │         FormData: file, category, remarks    → { id, ... }  │
 * │  DELETE /api/attachments/:empCode/:id       → { success }   │
 * └──────────────────────────────────────────────────────────────┘
 */
function AttachmentsScreen({ empName, empCode, onClose, opts, readOnly }) {
  const [companyCode] = useState(
    () =>
      sessionStorage.getItem('companyCode') ||
      localStorage.getItem('companyCode') ||
      '',
  );
  const [entityCode] = useState(() => localStorage.getItem('entityCode') || '');
  const [files, setFiles] = useState([]);
  const [category, setCategory] = useState('');
  const [remarks, setRemarks] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const activeFetchKeyRef = useRef(null);
  const fileInputRef = useRef();
  const getOptionValue = option =>
    option?.value || option?.Value || option?.Code || option?.code || '';
  const getOptionLabel = option =>
    option?.label ||
    option?.Label ||
    option?.Description ||
    option?.description ||
    getOptionValue(option);
  const getCategoryLabel = rawValue => {
    if (!rawValue) return '';
    const match = (opts?.attachmentTypes || []).find(
      o => getOptionValue(o) === rawValue,
    );
    return match ? getOptionLabel(match) : rawValue;
  };
  const toDisplayDate = value => {
    const raw = typeof value === 'string' ? value : '';
    if (!raw) return '';
    return raw.includes('T') ? raw.split('T')[0] : raw;
  };
  const mapAttachmentFromApi = item => {
    const typCode = clean(
      item?.typ || item?.Typ || item?.category || item?.cat || '',
    );
    const parsedRemarks = splitAttachmentRemarks(
      item?.Remarks || item?.remarks,
    );
    const pathRaw = item?.Path || item?.path || '';
    const fileFromPath = pathRaw ? String(pathRaw).split(/[\\/]/).pop() : '';
    const srRaw = item?.srNo ?? item?.SrNo;
    const srNo =
      srRaw === '' || srRaw === undefined || srRaw === null ? 0 : Number(srRaw);
    return {
      id: item?.id ?? srRaw ?? null,
      srNo,
      typ: typCode,
      category: parsedRemarks.categoryCode || typCode,
      fileName: item?.fileName || item?.file_name || fileFromPath || '',
      fileSize: item?.fileSize || item?.file_size || '—',
      remarks: parsedRemarks.remarks,
      uploadDate: toDisplayDate(
        item?.uploadDate ||
          item?.upload_date ||
          item?.UploadDate ||
          item?.EntryDt ||
          item?.createdOn ||
          '',
      ),
      path: pathRaw,
    };
  };

  const loadAttachments = useCallback(() => {
    if (!empCode) return;
    const payload = { employeeId: empCode };
    const fetchKey = JSON.stringify(payload);
    if (activeFetchKeyRef.current === fetchKey) return;

    const request = apiCall('/envision/api/employee/get-attachments', {
      method: 'POST',
      body: payload,
    });
    if (!request) return;

    activeFetchKeyRef.current = fetchKey;
    setLoading(true);
    request
      .then(data => {
        const rows = Array.isArray(data?.result)
          ? data.result
          : Array.isArray(data)
            ? data
            : [];
        setFiles(rows.map(mapAttachmentFromApi));
        setLoading(false);
      })
      .catch(err => {
        console.error('Load attachments:', err);
        setError('Failed to load attachments.');
        setLoading(false);
      })
      .finally(() => {
        if (activeFetchKeyRef.current === fetchKey) {
          activeFetchKeyRef.current = null;
        }
      });
  }, [empCode]);

  useEffect(() => {
    loadAttachments();
  }, [loadAttachments]);

  const handleFileSelect = e => {
    if (e.target.files[0]) setSelectedFile(e.target.files[0]);
  };
  const resetUpload = () => {
    setCategory('');
    setSelectedFile(null);
    setRemarks('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const doUpload = () => {
    if (!selectedFile || !category) return;
    if (!companyCode) {
      setError('Company code is missing. Please login again.');
      return;
    }
    if (!API_BASE_URL) {
      setFiles(p => [
        ...p,
        {
          id: Date.now(),
          category,
          fileName: selectedFile.name,
          fileSize: `${(selectedFile.size / 1024).toFixed(1)} KB`,
          remarks,
          uploadDate: new Date().toISOString().split('T')[0],
        },
      ]);
      resetUpload();
      return;
    }
    setError(null);
    // Send non-file fields before "file" so multer can read body when resolving the upload folder.
    const formData = new FormData();
    formData.append('employeeId', empCode);
    formData.append('typ', EMPLOYEE_ATTACHMENT_TYP);
    formData.append('reference', empCode);
    formData.append('srNo', '0');
    formData.append('coCd', localStorage.getItem('entityCode') || '');
    formData.append(
      'remarks',
      category
        ? `${category}|${(remarks || '').trim()}`
        : (remarks || '').trim(),
    );
    formData.append('file', selectedFile);

    apiClient
      .create(`${BASE_EMPLOYEE_URL}/create-attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(() => {
        resetUpload();
        loadAttachments();
      })
      .catch(err => {
        console.error('Upload attachment:', err);
        setError('Failed to upload.');
      });
  };

  const handleAttachmentDownload = useCallback(
    async f => {
      const name = f.fileName || f.file_name;
      if (!name || !API_BASE_URL) return;
      try {
        setError(null);
        const blob = await apiClient.create(
          `${BASE_EMPLOYEE_URL}/download-attachments`,
          {
            typ: clean(f?.typ) || EMPLOYEE_ATTACHMENT_TYP,
            reference: empCode,
            srNo: f?.srNo || f?.id || 0,
            coCd: entityCode || companyCode,
            companyCode: entityCode || companyCode,
            entityCode: entityCode || companyCode,
          },
          {
            responseType: 'blob',
          },
        );
        FileUtil.downloadBlobFile(blob, name);
      } catch (e) {
        console.error(e);
        setError('Failed to download file.');
      }
    },
    [empCode, entityCode, companyCode],
  );

  const handleAttachmentView = useCallback(
    async f => {
      const name = f.fileName || f.file_name;
      if (!name || !API_BASE_URL) return;
      try {
        setError(null);
        const blob = await apiClient.create(
          `${BASE_EMPLOYEE_URL}/download-attachments`,
          {
            typ: clean(f?.typ) || EMPLOYEE_ATTACHMENT_TYP,
            reference: empCode,
            srNo: f?.srNo || f?.id || 0,
            coCd: entityCode || companyCode,
            companyCode: entityCode || companyCode,
            entityCode: entityCode || companyCode,
          },
          {
            responseType: 'blob',
          },
        );
        const asBlob = new Blob([blob], {
          type: blob.type || 'application/pdf',
        });
        setPreviewFile(new File([asBlob], name, { type: asBlob.type }));
        setPreviewOpen(true);
      } catch (e) {
        console.error(e);
        setError('Failed to open file preview.');
      }
    },
    [empCode, entityCode, companyCode],
  );

  const openDeleteConfirm = i => {
    setDeleteIndex(i);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    if (deleteLoading) return;
    setDeleteModalOpen(false);
    setDeleteIndex(null);
  };

  const performDelete = () => {
    if (deleteIndex === null || deleteIndex === undefined) return;
    const i = deleteIndex;
    const target = files[i];

    const finishOk = () => {
      setDeleteLoading(false);
      setDeleteModalOpen(false);
      setDeleteIndex(null);
    };

    setDeleteLoading(true);
    apiClient
      .create(`${BASE_EMPLOYEE_URL}/delete-attachments`, {
        typ: target?.typ || EMPLOYEE_ATTACHMENT_TYP,
        reference: empCode,
        srNo: target?.srNo || target?.id || 0,
        coCd: entityCode || companyCode,
        companyCode: entityCode || companyCode,
        entityCode: entityCode || companyCode,
      })
      .then(() => {
        setFiles(p => p.filter((_, j) => j !== i));
        finishOk();
      })
      .catch(err => {
        console.error('Delete attachment:', err);
        setError('Failed to delete.');
        setDeleteLoading(false);
      });
  };

  return (
    <div className="ov">
      <div
        className="sub-modal sub-modal-lg"
        onClick={e => e.stopPropagation()}
      >
        <div className="sub-modal-top comp-modal-top">
          <div className="sub-modal-title">
            <div className="sub-modal-title-row sub-modal-title-row--stacked comp-modal-title-row">
              <span className="sub-modal-icon sub-modal-icon--lg comp-modal-icon-shell">
                <span className="comp-modal-icon-inner">
                  {SUB_SCREENS[6].icon}
                </span>
              </span>
              <div className="sub-modal-title-block">
                <h2>Attachments</h2>
                {empCode && (
                  <span className="sub-modal-emp sub-modal-emp--stacked">
                    {[empName, empCode].filter(Boolean).join(' \u00b7 ')}
                  </span>
                )}
              </div>
            </div>
          </div>
          <button className="mdl-x" onClick={onClose} type="button">
            <CloseIcon />
          </button>
        </div>
        <div className="sub-modal-body">
          {error && (
            <div
              style={{
                padding: '12px 20px',
                background: 'var(--red-l)',
                color: 'var(--red)',
                fontSize: 13,
                fontWeight: 500,
                marginBottom: 12,
                borderRadius: 'var(--r)',
              }}
            >
              {error}
            </div>
          )}
          {!readOnly && (
            <div className="dep-form" style={{ marginBottom: 16 }}>
              <div className="dep-form-title">Upload Attachment</div>
              <div
                style={{
                  padding: '18px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 14,
                }}
              >
                <div className="fg">
                  <label className="my-1 form-label">
                    Category<span className="text-danger">*</span>
                  </label>
                  <div className="sw">
                    <select
                      className="form-select external-filter-input  form-select"
                      value={category}
                      onChange={e => setCategory(e.target.value)}
                    >
                      <option value="">Select Category</option>
                      {opts?.attachmentTypes?.map(o => (
                        <option
                          key={getOptionValue(o) || getOptionLabel(o)}
                          value={getOptionValue(o)}
                        >
                          {getOptionLabel(o)}
                        </option>
                      ))}
                    </select>
                    <svg
                      className="chev"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      style={{ display: 'none' }}
                    >
                      <path
                        d="M6 9l6 6 6-6"
                        stroke="#94a3b8"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </div>
                <div className="fg">
                  <label className="my-1 form-label">
                    Choose File<span className="text-danger">*</span>
                  </label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="form-control external-filter-input  form-control"
                    onChange={handleFileSelect}
                    style={{ padding: 8 }}
                  />
                </div>
                <div className="fg">
                  <label className="my-1 form-label">Remarks</label>
                  <input
                    className="form-control external-filter-input  form-control"
                    value={remarks}
                    onChange={e => setRemarks(e.target.value)}
                    placeholder="Enter remarks for this attachment"
                  />
                </div>
              </div>
              <div className="dep-form-actions">
                <button className="btn em-btn" onClick={resetUpload}>
                  Clear
                </button>
                <button
                  className="btn em-btn em-btn-p"
                  onClick={doUpload}
                  disabled={!selectedFile || !category}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"
                      stroke="#fff"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Upload
                </button>
              </div>
            </div>
          )}
          {loading ? (
            <div className="sub-empty">
              <span>Loading attachments...</span>
            </div>
          ) : files.length > 0 ? (
            <div className="dep-table-wrap">
              <table className="dep-table dep-table--attachments">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Category</th>
                    <th>File Name</th>
                    <th>Remarks</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {files.map((f, i) => (
                    <tr key={`${f.typ || f.category}-${f.srNo}-${i}`}>
                      <td>{i + 1}</td>
                      <td>{getCategoryLabel(f.category) || '—'}</td>
                      <td className="dep-table__file-cell">
                        {f.fileName || f.file_name}
                      </td>
                      <td className="dep-table__remarks-cell">
                        {f.remarks || '—'}
                      </td>
                      <td>
                        <div className="dep-acts">
                          <button
                            type="button"
                            className="att-ab view"
                            title="View"
                            disabled={!(f.fileName || f.file_name)}
                            onClick={() => handleAttachmentView(f)}
                          >
                            <Icon path={mdiEye} size={0.75} color="#017EBD" />
                          </button>
                          <button
                            type="button"
                            className="att-ab dl"
                            title="Download"
                            disabled={!(f.fileName || f.file_name)}
                            onClick={() => handleAttachmentDownload(f)}
                          >
                            <Icon
                              path={mdiDownload}
                              size={0.75}
                              color="#017EBD"
                            />
                          </button>
                          <button
                            type="button"
                            className="att-ab del"
                            onClick={() => openDeleteConfirm(i)}
                            disabled={readOnly}
                            title={readOnly ? 'View only' : 'Delete'}
                          >
                            <DeleteIcon
                              color={readOnly ? '#94a3b8' : '#ef4444'}
                            />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="sub-empty">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                <path
                  d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"
                  stroke="#cbd5e1"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>
                {readOnly
                  ? 'No attachments found.'
                  : 'No attachments found. Upload a file above.'}
              </span>
            </div>
          )}
        </div>
      </div>
      <DisplayModal
        isModalOpen={previewOpen}
        toggle={() => {
          setPreviewOpen(false);
          setPreviewFile(null);
        }}
        errorMessage="View attachment"
        file={previewFile}
        zIndex={21001}
      />
      <EmployeeMasterDeleteConfirm
        isOpen={deleteModalOpen}
        toggle={closeDeleteModal}
        onConfirm={performDelete}
        loading={deleteLoading}
        message={
          <>
            Are you sure you want to delete the{' '}
            <span className="em-delete-confirm__highlight">
              {emBasenameOnly(
                deleteIndex != null && files[deleteIndex]
                  ? files[deleteIndex].fileName || files[deleteIndex].file_name
                  : '',
              )}
            </span>{' '}
            document?
          </>
        }
      />
    </div>
  );
}

export default AttachmentsScreen;
